"use client";

import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const toggleVariants = cva(
  `
    inline-flex items-center
    justify-center gap-2
    rounded-md text-sm
    font-medium
    whitespace-nowrap
    transition-[color,box-shadow]
    outline-none hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none
    disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20
    data-pressed:bg-accent
    data-pressed:text-accent-foreground dark:aria-invalid:ring-destructive/40
    [&_svg]:pointer-events-none [&_svg]:shrink-0
    [&_svg:not([class*='size-'])]:size-4
  `,
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: `
          border
          border-input bg-transparent
          shadow-xs hover:bg-accent hover:text-accent-foreground
        `,
      },
      size: {
        default: "h-9 min-w-9 px-2",
        sm: "h-8 min-w-8 px-1.5",
        lg: "h-10 min-w-10 px-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Toggle({
  className,
  variant,
  size,
  nativeButton = false,
  ...props
}: React.ComponentProps<typeof TogglePrimitive> &
  VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive
      data-slot="toggle"
      nativeButton={nativeButton}
      className={(state) =>
        cn(
          toggleVariants({ variant, size }),
          resolveClassName(className, state),
        )
      }
      {...props}
    />
  );
}

export { Toggle, toggleVariants };
